import { Html, Line } from '@react-three/drei'
import React, { FC, useRef } from 'react'
import styled from 'styled-components'
import { Vector3 } from 'three'
import { KVAnnotation, WorldPosition } from 'types'

import {
  ANNOTATION_Y_OFFSET,
  AxisConfig,
  AxisSource,
} from './utils/annotations'

const AnnotationLabel = styled.div`
  background-color: rgba(10, 10, 10, 0.85);
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  pointer-events: none;
  user-select: none;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`

const AreaText = styled.span`
  font-size: 10px;
  font-weight: 400;
  opacity: 0.85;
  margin-left: 5px;
`

/** Format a number with commas as thousands separators */
function formatArea(area: number): string {
  return area.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

interface AnnotationMarkerProps {
  annotation: KVAnnotation
  axisConfig: AxisConfig
  showLabels: boolean
  showArea: boolean
}

/**
 * Simple annotation marker - plots coordinates with configurable axis mapping
 */
const AnnotationMarker: FC<AnnotationMarkerProps> = ({
  annotation,
  axisConfig,
  showLabels,
  showArea,
}) => {
  const positionRef = useRef<Vector3 | null>(null)

  if (!annotation.geometry) return null

  // Configurable axis mapping with Y offset to float above mesh
  const mapToThree = (p: { x: number; y: number; z: number }): Vector3 => {
    const getValue = (axis: AxisSource) => {
      switch (axis) {
        case 'x':
          return p.x
        case 'y':
          return p.y
        case 'z':
          return p.z
      }
    }
    return new Vector3(
      getValue(axisConfig.xFrom) * axisConfig.xSign,
      getValue(axisConfig.yFrom) * axisConfig.ySign - ANNOTATION_Y_OFFSET,
      getValue(axisConfig.zFrom) * axisConfig.zSign
    )
  }

  // Handle point annotations
  if (annotation.geometry.kind === 'point' && annotation.geometry.position) {
    const pos = parseWorldPosition(annotation.geometry.position)
    if (!pos) return null

    const position = mapToThree(pos)
    positionRef.current = position

    return (
      <group>
        <mesh position={position}>
          <sphereGeometry args={[0.4, 16, 16]} />
          <meshStandardMaterial
            color='#ffffff'
            emissive='#ffffff'
            emissiveIntensity={1}
          />
        </mesh>
        {showLabels && (
          <Html position={position} center>
            <AnnotationLabel>
              {annotation.label || annotation.type}
            </AnnotationLabel>
          </Html>
        )}
      </group>
    )
  }

  // Handle polygon annotations
  if (annotation.geometry.kind === 'polygon' && annotation.geometry.points) {
    const points = annotation.geometry.points
      .map(parseWorldPosition)
      .filter((p): p is { x: number; y: number; z: number } => p !== null)

    if (points.length < 3) return null

    const vectors = points.map(mapToThree)
    const center = new Vector3()
    vectors.forEach((v) => center.add(v))
    center.divideScalar(vectors.length)
    positionRef.current = center

    const labelText = annotation.label || annotation.type

    return (
      <group>
        <Line points={[...vectors, vectors[0]]} color='#ffffff' lineWidth={3} />
        {showLabels && (
          <Html position={center} center>
            <AnnotationLabel>
              {labelText}
              {showArea && annotation.area && (
                <AreaText>{formatArea(annotation.area)} m²</AreaText>
              )}
            </AnnotationLabel>
          </Html>
        )}
      </group>
    )
  }

  return null
}

interface ReadOnly3DTilesAnnotationsProps {
  annotations: KVAnnotation[]
  axisConfig: AxisConfig
  showLabels?: boolean
  showArea?: boolean
}

export const ReadOnly3DTilesAnnotations: FC<
  ReadOnly3DTilesAnnotationsProps
> = ({ annotations, axisConfig, showLabels = true, showArea = true }) => {
  const visibleAnnotations = annotations.filter((a) => a.visible)

  if (visibleAnnotations.length === 0) {
    return null
  }

  return (
    <group>
      {visibleAnnotations.map((annotation) => (
        <AnnotationMarker
          key={annotation.id}
          annotation={annotation}
          axisConfig={axisConfig}
          showLabels={showLabels}
          showArea={showArea}
        />
      ))}
    </group>
  )
}

function parseWorldPosition(
  pos: WorldPosition | undefined
): { x: number; y: number; z: number } | null {
  if (!pos) return null

  if (typeof pos === 'object' && 'x' in pos && 'y' in pos && 'z' in pos) {
    const x = Number(pos.x)
    const y = Number(pos.y)
    const z = Number(pos.z)
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      return null
    }
    return { x, y, z }
  }

  return null
}
